import { Link } from "react-router-dom";
import { Leaf, Sprout, Compass, Heart, Mail, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1707451811841-0f881783cde8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjB3b3Jrc2hvcCUyMGZvcmVzdCUyMGdhdGhlcmluZ3xlbnwwfHx8fDE3NzgwNjA3MjF8MA&ixlib=rb-4.1.0&q=85";

export default function About() {
  return (
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="about-page">
      <section className="grid md:grid-cols-12 gap-10 mb-24 items-center">
        <div className="md:col-span-7">
          <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-4">Chi siamo</div>
          <h1 className="font-serif text-5xl md:text-7xl text-tc-primary leading-[0.95] mb-6">
            Una rete che<br/>
            <em className="text-tc-secondary italic">germoglia</em>
          </h1>
          <p className="text-lg text-tc-muted leading-relaxed font-light">
            Terra Comune è nata dall'idea che le persone, i progetti e le comunità che cercano una vita più giusta,
            sostenibile e connessa con la Terra non sono dei singoli puntini sparsi.
            Sono lo stesso filo, lo stesso bosco, la stessa radice.
          </p>
        </div>
        <div className="md:col-span-5">
          <div className="absolute -translate-y-4 -translate-x-4 w-32 h-32 rounded-full bg-tc-accent/20 blur-2xl" />
          <img src={HERO} alt="Cerchio nel bosco" className="relative rounded-[28px] w-full h-[400px] object-cover shadow-2xl" />
        </div>
      </section>

      {/* Manifesto */}
      <section className="bg-tc-surface rounded-[32px] p-10 md:p-16 mb-24" data-testid="about-manifesto">
        <div className="max-w-3xl mx-auto text-center">
          <Leaf className="w-8 h-8 text-tc-primary mx-auto mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl text-tc-primary leading-tight italic mb-8">
            "Non siamo individui separati.<br/>Siamo radice."
          </h2>
          <p className="text-tc-muted leading-relaxed text-lg">
            Tutto comincia da una semplice intuizione: l'individualismo che ci viene insegnato fin da piccoli
            è una bugia comoda, perché tiene il sistema in piedi. La verità è che non esistiamo da soli.
            Esistiamo dentro un tessuto — fatto di altre persone, di acqua, di vento, di terra.
            Quando uno di noi si spezza, qualcosa nel tessuto si strappa.
          </p>
          <p className="text-tc-muted leading-relaxed text-lg mt-5">
            Terra Comune è un atlante di chi ha già cominciato a vedersi così. Ecovillaggi che vivono insieme,
            reti olistiche, comunità di vicinato, progetti di permacultura, cooperative del cambiamento —
            tutti diversi, tutti collegati. Tutti fatti dalla stessa radice.
          </p>
        </div>
      </section>

      {/* I tre principi */}
      <section className="mb-24">
        <h2 className="font-serif text-4xl md:text-5xl text-tc-primary mb-12 text-center">Tre principi che ci guidano</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Pillar
            icon={<Sprout className="w-6 h-6" />}
            title="Coltivare invece di consumare"
            text="Tornare a fare con le mani: pane, sapone, orto, abiti, parole. Ogni gesto autoprodotto è un piccolo atto politico."
          />
          <Pillar
            icon={<Compass className="w-6 h-6" />}
            title="Comunità invece di individuo"
            text="Decidere insieme, sbagliare insieme, ricominciare insieme. La differenza non è un peso, è la forza dell'ecosistema."
          />
          <Pillar
            icon={<Heart className="w-6 h-6" />}
            title="Cura invece di profitto"
            text="Misurare il valore non in soldi ma in relazioni, salute, tempo, biodiversità. Il PIL non sa nulla del bosco."
          />
        </div>
      </section>

      {/* Storia */}
      <section className="grid md:grid-cols-12 gap-10 mb-24">
        <div className="md:col-span-4">
          <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-3">La storia</div>
          <h2 className="font-serif text-4xl text-tc-primary leading-tight">Come è nata Terra Comune</h2>
        </div>
        <div className="md:col-span-8 space-y-5 text-tc-muted leading-relaxed">
          <p>
            Tutto è cominciato da un video — scienziati, studiosi, contadini, persone semplici che parlavano
            della stessa cosa: ricominciare da zero non è distruggere, è tornare alla radice.
          </p>
          <p>
            E poi una mappa, quella della Rete Olistica di Livorno su Italia che Cambia,
            che mostrava decine di realtà a pochi chilometri una dall'altra che si ignoravano.
            Era chiaro che mancava un luogo unico dove tutto questo potesse trovarsi, vedersi, parlarsi.
          </p>
          <p>
            Terra Comune nasce da lì: un atlante vivo che raccoglie ecovillaggi RIVE, reti olistiche,
            comunità di vicinato, progetti permaculturali, cooperative etiche.
            Più di 60 realtà sulla mappa al momento del lancio, ma la mappa non è mai chiusa —
            ogni struttura può iscriversi, e ogni persona può unirsi alla comunità.
          </p>
        </div>
      </section>

      {/* Contatti / Newsletter CTA */}
      <section className="bg-tc-primary rounded-[32px] p-10 md:p-16 text-center" data-testid="about-cta">
        <p className="text-tc-bg/70 text-xs tracking-[0.3em] uppercase mb-4">Cammina con noi</p>
        <h2 className="font-serif text-4xl md:text-5xl text-tc-bg leading-tight mb-6 max-w-2xl mx-auto">
          Sei già parte di tutto questo.<br/>
          <em className="italic">Vuoi rendertene conto?</em>
        </h2>
        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <Link to="/realta" className="bg-tc-bg text-tc-primary px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="about-cta-realta">
            Esplora le realtà <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/login" className="border border-tc-bg/40 text-tc-bg px-7 py-3.5 rounded-full font-medium hover:bg-tc-bg/10 transition" data-testid="about-cta-join">
            Unisciti alla comunità
          </Link>
        </div>
        <a href="mailto:info@terracomune.org" className="inline-flex items-center gap-2 text-tc-bg/70 text-sm mt-10 hover:text-tc-bg transition" data-testid="about-contact-email">
          <Mail className="w-4 h-4" /> info@terracomune.org
        </a>
      </section>
    </main>
  );
}

function Pillar({ icon, title, text }) {
  return (
    <div className="bg-tc-surface rounded-3xl p-8 border border-tc/40 hover:-translate-y-1 transition">
      <div className="text-tc-secondary mb-5">{icon}</div>
      <h3 className="font-serif text-2xl text-tc-primary mb-3 leading-tight">{title}</h3>
      <p className="text-sm text-tc-muted leading-relaxed">{text}</p>
    </div>
  );
}
